import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { L } from '@ofself/shell/editor/lucide-icons.js'
import { Shell, ShellProvider, CloseIcon, LinkIcon, CopyIcon, PenIcon, CheckIcon as CommitIcon, TagIcon, PeopleIcon, PlusIcon, FilterIcon, CollapseAllIcon, ExpandAllIcon, PanelHeader, PanelMenu, IdentityWall, SearchIcon, SplitView, PanelDrawer, Splash, LibraryDrawer, LibraryRow, LibraryPanel, CollectionDrawer, RoomArrival, ContextMenu, Tip, TipProvider, useListSelection, useMoveFlow, SelectionBar, AtriumOrder, countOf, setFocusMode, useAppFigureSvg, useWallOpen, LucideGlyph } from '@ofself/shell'
import ShareSheet from './ShareSheet.jsx'
import ExportSheet from './ExportSheet.jsx'
import Home from './Home.jsx'
import { Button } from '@ofself/shell'
import { api } from './api.js'
import { useWordStore, isLocal, bySort } from './store.js'
import { byLetter, filterWords, avoided, inheritedOf } from './model.js'
import { inheritedStance } from '../../lib/move.mjs'
import { makeMover } from './moving.js'
import WordSheet from './WordSheet.jsx'
import Find from './Find.jsx'
import { DICT_SETTINGS, usePrefs, ABSORBED_EVENT } from './Settings.jsx'
import { committedTerms, downloadJson, downloadMarkdownFiles } from './transfer.js'
import Door from './Door.jsx'
import { useDictionaryHelp } from './Help.jsx'
import { qh } from './qh.js'
import { agentSend, agentSessions, agentResume, resetAgentThread, agentStop } from './agent.js'

/* ONE PAGE, AND THAT IS THE APP (owner call 2026-09-18, "delete the
   lexicographer page. no need for it"): the list, and the word's page beside
   it. A dictionary is a list of words and what they mean; anything else here
   would be a second app wearing this one's name. */
const PAGES = [{ id: 'words', label: 'Dictionary' }]
const HOME = 'words'
const IDENTITY_KEY = 'dictionary-identity'

// STATE THAT OUTLIVES A RELOAD (owner call 2026-09-22: "when refreshing the
// page, any sidebar state should remain where it is"). useState, read once from
// this device's storage and written back on every change; updaters work as
// they do with useState. A value that cannot be read falls back to `initial`.
function useStored(key, initial) {
  const [value, setValue] = useState(() => {
    try { const raw = localStorage.getItem(key); return raw == null ? initial : JSON.parse(raw) } catch { return initial }
  })
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(value)) } catch { /* private window */ } }, [key, value])
  return [value, setValue]
}
const STANDINGS = [['all', 'Every word'], ['committed', 'Committed'], ['proposed', 'Offered'], ['shared', 'Shared with you'], ['avoided', 'Anti-words']]
// the address segment a friend's word lives under: their name, as the
// address law reads it (/identity/word) — the id only when they have none
const ownerSlug = o => (o && (o.username || o.user_id)) || ''

/* THE ADDRESS IS /username/word (owner call 2026-09-18). It is a READING of
   the page, never its identity: underneath, a page is addressed by the word's
   id, so a title typed letter by letter — whose slug changes with every
   keystroke — can never lose the page. The URL follows the word (replaceState
   as the slug moves); an old /word/<id> link still resolves. */
/* THE ADDRESS NAMES THE IDENTITY (owner call 2026-09-18): /username is your
   own dictionary, /group-name is the group's, and a word is /identity/word.
   Signed in, the root goes straight to /username. */
const pathOf = () => {
  const p = window.location.pathname.replace(/\/+$/, '')
  if (p === '/help') return { page: 'help', at: null }   // the manual — the app's own page, not an identity
  let m = /^\/word\/(.+)$/.exec(p)
  if (m) return { page: 'words', at: decodeURIComponent(m[1]) }
  m = /^\/([^/]+)\/([^/]+)$/.exec(p)
  if (m) return { page: 'words', who: decodeURIComponent(m[1]), at: decodeURIComponent(m[2]) }
  m = /^\/([^/]+)$/.exec(p)
  if (m) return { page: HOME, who: decodeURIComponent(m[1]), at: null }
  return { page: HOME, at: null }
}
const NEW = 'new'   // the address of a word that has no name yet

/* THE ROW'S VERBS (owner call 2026-09-18): every word in the list carries its
   grave and its ordinary verbs — behind an eclipse that stands up on hover,
   and behind the right button, the same menu either way. The kit's own
   Menu and ContextMenu, so it is the house menu and not a new one. */
/* a filter row: the name, its count at the far edge in the faint mono, and
   the check beyond it when it is the one in force */
// the kit's lucide glyphs, as the editor draws them — a menu row's icon


/* RENAME IN PLACE (owner call 2026-09-19: "a rename button in the sidebar
   context menu — clicking it should activate the naming field in the
   sidebar"): the kit's <LibraryRow renaming> — the row IS the field while it
   renames, raised by the menu or by Return on the one row chosen. Return
   keeps, Esc puts the old name back, leaving keeps. */
/* ONE ROW RE-RENDERS, NOT THE LIST (audit 2026-10-02: with 1,500 words every
   keystroke in a definition re-rendered 1,500 rows — each a Radix context menu
   and a ⋯ dropdown — ~120ms of React per key, typing visibly lagged). A row is
   memoized on what it shows: the word (the store keeps an untouched word's
   object as it was), whether it is open, marked or renaming, how many words a
   marked row speaks for, and whether a group stands. Its verbs are read from
   `act` — a ref App refreshes every render — when the row renders and when a
   verb is pressed, so a row that skipped a render never acts on stale state. */
const WordRow = memo(function WordRow({ w, active, marked, flying, renaming, batchN, grouped, act }) {
  const items = act.current.rowItems(w)
  const onOpen = () => act.current.go('words', w.id)
  const onRename = name => act.current.renameWord(w, name)
  const onRenameInput = name => act.current.renameInput(w, name)
  const cls = `dt-row${active ? ' is-active' : ''}${w.status === 'proposed' ? ' is-proposed' : ''}${w.owner ? ' is-shared' : ''}`
  // data-id is what the kit's marquee hit-tests against; data-marked is what
  // it wears when the rectangle has crossed it
  // A FRIEND'S WORD WEARS THEIR NAME (owner call 2026-09-19): the row's meta —
  // the kit's own, which never breaks — so whose sense it is reads at a glance
  // the row's ⋯ is the kit's (ContextMenu `more`) — the same items a right-click opens
  return <ContextMenu items={items} more moreLabel={`More for ${w.name}`} trigger={
    <div className={cls}
      {...qh(w.name, w.owner
        ? `${ownerSlug(w.owner)}'s word, shared with you${w.access === 'edit' ? ' to read and edit' : ', read-only'}. Click to open it. Right-click to share it onward to a group, copy its link, or copy it into your own dictionary.`
        : `${w.status === 'proposed' ? 'Offered by the Lexicographer and not yet committed — it stands dim until you commit it. ' : 'A committed word in your dictionary. '}Click to open its page. Right-click for rename, share, copy link, duplicate, export${w.status === 'proposed' ? ', commit' : ''} and delete. Marked words act together.`)}>
        {/* the shell's selection: data-id, the marks, ⌘/⇧-click, the drag (useListSelection) */}
        <LibraryRow {...act.current.sel.rowProps(w.id, { onClick: onOpen })} name={w.name || 'Untitled'} active={active} dim={w.status === 'proposed'}
          meta={w.owner ? ownerSlug(w.owner) : undefined} renaming={renaming} onRename={onRename} onRenameInput={onRenameInput}
          className={w.owner ? 'is-shared' : ''} />
    </div>
  } />
})

const RAIL = ['logo', 'personas', 'search']

export default function App() {
  const [me, setMe] = useState(null)
  const store = useWordStore()
  const { words, error, setError } = store
  const [loading, setLoading] = useState(true)
  const [{ page, at }, setRoute] = useState(pathOf)
  // THE MANUAL'S PARTS (kit useHelpPage): its index rides the list's own
  // column on /help, its body the word's place
  const onHelp = page === 'help'
  const help = useDictionaryHelp(() => go(HOME))
  const [standing, setStanding] = useStored('dictionary:standing', 'all')
  // NARROWING BY PROPERTY (owner call 2026-09-18): the same filter menu holds
  // the standing, the type, and every tag the dictionary carries, with counts
  const [tagFilter, setTagFilter] = useStored('dictionary:lens', [])
  // (definition TYPES ripped out 2026-09-19, owner call: "people can just add
  // their own tags" — the filter narrows by standing and tag, and by WHOSE:
  // '' is anyone, 'you' your own words, else one friend)
  const [fromFilter, setFromFilter] = useStored('dictionary:from', '')
  const narrowed = standing !== 'all' || tagFilter.length > 0 || !!fromFilter
  // A DICTIONARY IS NOT A FILTER (owner call 2026-09-22): being in one, the
  // lens, says so in the head's title alone. Only a filter ON TOP of it (a
  // standing, whose, or several tags at once) earns the chip row and the dot,
  // and clearing it keeps you in the dictionary.
  const filtered = standing !== 'all' || !!fromFilter || tagFilter.length > 1
  const clearFilter = () => { setStanding('all'); setFromFilter(''); if (tagFilter.length > 1) setTagFilter([]) }
  // WHAT LANDED: a line under the list head that says what a drop or a pass
  // just did, because a list that silently grew is a list you doubt
  const [notice, setNotice] = useState('')
  const [dropping, setDropping] = useState(false)
  const [findOpen, setFindOpen] = useState(false)
  // THE SETTINGS ARE THE SHELL'S WINDOW (owner call 2026-09-19): the rail's cog
  // opens the kit's own Settings; this app contributes panes and reads the
  // same prefs store they write to.
  const [prefs, savePrefs] = usePrefs()
  const [zen, setZen] = useStored('dictionary:focus', false)
  // the Personas wall's open state is the shell's, remembered with its tab
  const [identityOpen, setIdentityOpen] = useWallOpen(IDENTITY_KEY)
  // THE LEXICOGRAPHER IN THE PANEL (owner report 2026-09-19: "clicking start
  // new conversation doesn't do anything"). NULL, NOT []: the wall reads []
  // as "this app offers no agents" and says so for the length of the fetch;
  // null means not known yet and draws nothing. Only a truthy answer is kept.
  const [identityAgents, setIdentityAgents] = useState(null)
  useEffect(() => {
    if (!me || !me.authorized) return
    let live = true
    fetch('/api/identity/agents').then(r => r.ok ? r.json() : null).then(j => { if (live && j && Array.isArray(j.agents)) setIdentityAgents(j.agents) }).catch(() => {})
    return () => { live = false }
  }, [me])
  const [folded, setFolded] = useState(() => { try { return localStorage.getItem('dictionary:list-folded') === 'true' } catch { return false } })
  const fold = next => { setFolded(next); try { localStorage.setItem('dictionary:list-folded', String(next)) } catch { /* private window */ } }
  // EVERY LETTER IS A DRAWER (owner call 2026-09-18, synapse's sidebar
  // grammar). Open by default — a dictionary you can read at a glance beats
  // one you have to unfold — and only the letters you SHUT are remembered,
  // so a new letter arriving is open like the rest.
  const [shutLetters, setShutLetters] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('dictionary:shut-letters') || '[]')) } catch { return new Set() }
  })
  const toggleLetter = letter => setShutLetters(prev => {
    const next = new Set(prev)
    next.has(letter) ? next.delete(letter) : next.add(letter)
    try { localStorage.setItem('dictionary:shut-letters', JSON.stringify([...next])) } catch { /* private window */ }
    return next
  })
  const setAllLetters = shut => {
    const next = shut ? new Set(byLetter(words).map(g => g.letter)) : new Set()
    setShutLetters(next)
    try { localStorage.setItem('dictionary:shut-letters', JSON.stringify([...next])) } catch { /* private window */ }
  }

  // THE TAB WEARS THE REGISTER'S FIGURE TOO (owner call 2026-09-19: "shouldn't
  // they all show one source of truth"). The mark used to be drawn twice — the
  // register's for the seats, a local Letter for the door and the tab — and
  // the two had already diverged. One figure now, read from the kit.
  // (shell 0.289: the figure is the app record's logo and arrives async — the
  // hook re-renders when it lands)
  const figure = useAppFigureSvg('dictionary')
  useEffect(() => {
    try {
      const svg = figure; if (!svg) return
      let link = document.querySelector('link[rel="icon"]')
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link) }
      link.type = 'image/svg+xml'; link.href = 'data:image/svg+xml,' + encodeURIComponent(svg)
    } catch (e) { /* a tab without an icon is still a tab */ }
  }, [figure])
  // THE SHARE'S RETURN LEG. Paradigm's consent page hands the browser back
  // here with the signed state it was given; this posts the finalize so the
  // word's own registry records who it now stands open to, then wipes the
  // query so a reload cannot replay it.
  // ONLY A CONFIRMED CONSENT COUNTS (2026-09-19): the page appends
  // `code=success` when the person approved and `denied=true` when they
  // pressed Deny — and this leg used to record a share on either. And the
  // finalize now answers whether the word actually REACHES them, asked of the
  // platform as the recipient: a consent page can say yes and a grant still
  // leave the word out, which is how a share arrived nowhere.
  useEffect(() => {
    const q = new URLSearchParams(location.search)
    const kind = q.get('shared')
    if (!kind) return
    const done = () => window.history.replaceState(null, '', location.pathname + location.hash)   // the hash is the shell's mobile page (#menu, #wall/…) — keep it
    // THE WORD'S NAME, ASKED FOR (audit 2026-10-02: "the word is shared."). The
    // return is a fresh page load, so this runs before the list has arrived —
    // and with no shelf on this device the list it closed over was empty. The
    // node id is on the address; the name is one read away.
    const named = api.word(q.get('node_id') || '').then(r => r.word?.name || 'the word', () => (words.find(w => w.id === q.get('node_id')) || {}).name || 'the word')
    const say = f => named.then(n => setNotice(f(n)))
    if (q.get('denied') === 'true') { say(n => `${n} was not shared — the consent page was declined.`); done(); return }
    if (q.get('dev') !== '1' && q.get('code') !== 'success') { say(n => `${n} was not shared — the consent page did not confirm it.`); done(); return }
    const body = {
      state: q.get('state'), node_id: q.get('node_id'), dev: q.get('dev') === '1',
      ...(kind === 'friend'
        ? { recipient: q.get('recipient'), access: q.get('access'), sharing_id: q.get('sharing_id') }
        : { realm_id: q.get('realm_id') }),
    }
    fetch(`/api/share/finalize-${kind}`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).then(r => r.json().catch(() => ({})).then(j => {
      if (!r.ok) { say(n => `${n} could not be recorded as shared: ${j.error || r.status}`); return }
      if (kind === 'friend' && j.reaches === false) say(n => `${n} is shared, but it is not reaching them yet — the platform's grant left it out. Share it again, or fix the share on the platform.`)
      else say(n => `${n} is shared.`)
    })).catch(e => say(n => `${n} could not be recorded as shared — ${e.message}. Share it again.`)).finally(done)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  // ⌘K reaches the same door the rail's SEARCH seat opens
  useEffect(() => {
    const onKey = e => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setFindOpen(v => !v) } }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  useEffect(() => { setFocusMode(zen); return () => setFocusMode(false) }, [zen])
  // THE DROP (owner call 2026-09-18): a document or a link dropped anywhere on
  // the app goes to the Lexicographer, which proposes the words it leans on.
  // Window-level, so no layout has to carry a target; the veil says what will
  // happen before it does.
  useEffect(() => {
    const wants = e => Array.from(e.dataTransfer?.types || []).some(t => t === 'Files' || t === 'text/uri-list')
    const over = e => { if (!wants(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDropping(true) }
    const leave = e => { if (!e.relatedTarget) setDropping(false) }
    const drop = async e => {
      if (!wants(e)) return
      e.preventDefault(); setDropping(false)
      const files = Array.from(e.dataTransfer.files || [])
      const uri = (e.dataTransfer.getData('text/uri-list') || '').split('\n').map(x => x.trim()).find(x => /^https?:\/\//.test(x))
      const say = (r, what) => { const n = (r.proposed || []).length; setNotice(n ? `${n} word${n === 1 ? '' : 's'} proposed from ${what}` : `nothing new in ${what}`); if (n) load() }
      try {
        if (files.length) { setNotice(`reading ${files.length === 1 ? files[0].name : files.length + ' files'}…`); for (const f of files) say(await api.absorbFile(f), f.name) }
        else if (uri) { setNotice(`reading ${uri}…`); say(await api.absorbUrl(uri), new URL(uri).hostname) }
      } catch (err) { setNotice(err.status === 428 ? 'the Lexicographer needs authorizing once — a tab opened; drop again after' : `could not read it: ${err.message}`) }
    }
    document.addEventListener('dragover', over); document.addEventListener('dragleave', leave); document.addEventListener('drop', drop)
    return () => { document.removeEventListener('dragover', over); document.removeEventListener('dragleave', leave); document.removeEventListener('drop', drop) }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const pop = () => setRoute(pathOf())
    window.addEventListener('popstate', pop)
    return () => window.removeEventListener('popstate', pop)
  }, [])

  // THE SPACES: personal, and every group — read once signed in, switched
  // through one door (/api/space), and named in the address
  const [spaces, setSpaces] = useState({ groups: [], personal: null })
  useEffect(() => { if (me?.authorized) api.accounts().then(setSpaces).catch(() => {}) }, [me?.authorized]) // eslint-disable-line react-hooks/exhaustive-deps
  const activeGroup = me?.groupId ? (spaces.groups.find(g => g.id === me.groupId) || { id: me.groupId, name: me.groupName || 'group', slug: (me.groupName || 'group').toLowerCase().replace(/[^a-z0-9]+/g, '-') }) : null
  const identitySlug = activeGroup ? activeGroup.slug : (me?.username || 'me')
  const userPath = () => encodeURIComponent(identitySlug)
  // the switch: the session moves, the address moves, the list reloads
  const switchSpace = async (g) => {
    await api.space(g ? g.id : null, g ? g.name : '').catch(() => {})
    window.location.assign(`/${encodeURIComponent(g ? g.slug : (me?.username || 'me'))}`)
  }
  // THE ADDRESS DECIDES THE SPACE on arrival: /group-name opens that group,
  // /username the personal dictionary, and the root becomes /username
  useEffect(() => {
    if (!me?.authorized) return
    // back from a group's consent page: /callback lands on /?group_id=… — enter that space
    const wanted = new URLSearchParams(window.location.search).get('group_id')
    if (wanted) { const g = spaces.groups.find(x => x.id === wanted); if (g) { switchSpace(g); return } }
    const { who, page: here } = pathOf()
    if (here === 'help') return   // the manual keeps its address
    if (!who) { window.history.replaceState(null, '', `/${encodeURIComponent(identitySlug)}${window.location.hash}`); return }   // keep the shell's mobile hash
    if (!spaces.groups.length && !spaces.personal) return
    const g = spaces.groups.find(x => x.slug === who)
    if (g && g.id !== me.groupId) { if (g.authorized) switchSpace(g); return }
    if (!g && who === me.username && me.groupId) switchSpace(null)
  }, [me?.authorized, spaces]) // eslint-disable-line react-hooks/exhaustive-deps
  // a friend's word is addressed under THEIR name — /mara/tidepool — as the
  // address law reads: /identity/word, whoever's identity it is
  const urlOf = w => `/${w && w.owner ? encodeURIComponent(ownerSlug(w.owner)) : userPath()}/${encodeURIComponent((w && w.slug) || NEW)}`
  const go = (nextPage, nextAt = null) => {
    if (at && at !== nextAt) store.sweep(at)   // an untitled, empty word left behind is not kept
    setRoute({ page: nextPage, at: nextAt })
    const w = nextAt && words.find(x => x.id === nextAt)
    const url = nextAt ? urlOf(w) : nextPage === HOME ? '/' : `/${nextPage}`
    window.history.pushState(null, '', url)
  }
  // THE ROUTE FOLLOWS AN ID SWAP (a local word born on the wire): same page,
  // same address, the identity underneath moves
  useEffect(() => store.onSwap((from, to) => {
    setRoute(r => (r.at === from ? { ...r, at: to } : r))
  }), []) // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => store.load().finally(() => setLoading(false)), [store.load]) // eslint-disable-line react-hooks/exhaustive-deps
  // the Absorb pane lives in the shell's settings window and cannot be handed
  // the loader — it says so through one event, and the list reloads
  useEffect(() => {
    const on = () => load()
    window.addEventListener(ABSORBED_EVENT, on)
    return () => window.removeEventListener(ABSORBED_EVENT, on)
  }, [load])

  useEffect(() => {
    api.me().then(m => { setMe(m); if (m.authorized) { load(); store.loadShared(); loadLexicons().then(migrateEmpties) } else setLoading(false) })
      .catch(e => { setMe({ authorized: false }); setError(e.message); setLoading(false) })
  }, [load]) // eslint-disable-line react-hooks/exhaustive-deps
  // WHAT FRIENDS SHARED, RE-READ ON RETURN: a grant given or widened while
  // this tab stood in the background shows on the next focus — at most once
  // a minute, which is as long as the server holds its answer anyway
  useEffect(() => {
    let at = Date.now()
    const back = () => { if (me?.authorized && Date.now() - at > 60000) { at = Date.now(); store.loadShared() } }
    window.addEventListener('focus', back)
    return () => window.removeEventListener('focus', back)
  }, [me?.authorized]) // eslint-disable-line react-hooks/exhaustive-deps


  /* the dictionary the editor marks with: COMMITTED words only, and one line of
     gloss for the dropdown. A SYNONYM IS A WORD OF ITS OWN (owner call
     2026-10-04), so it is marked and offered as itself — a stub with no words
     yet glosses with its source's, "via …". Only a synonym in another graph
     (another person's or a group's dictionary) rides as a second name on the
     word that links it: it has no row here to stand as. */
  // the words offered as you write: committed, and never the one being written
  // (a provisional row has no id yet, so the editor's selfId cannot exclude it)
  // — and never a friend's (owner call 2026-09-19): if you both define a word,
  // the mark would have to pick one; theirs is found by ⌘K, with a byline
  // a word answers to its name and its ALIASES (its own other forms), and to a
  // synonym held in another graph, which has no row of its own here
  const elsewhere = w => [...(w.aliases || []), ...(w.synonyms || []).filter(r => r.group || r.user).map(r => r.term)]
  const terms = useMemo(() => words.filter(w => w.status === 'committed' && !avoided(w) && w.name.trim() && w.id !== at && !w.owner).map(w => {
    const via = inheritedOf(w, words)
    return {
      id: w.id, slug: w.slug, term: w.name, aliases: elsewhere(w),
      status: w.status, committedAt: w.committedAt, name: w.name,
      text: w.text || via?.text || '', via: via ? via.name : null,
      gloss: (via ? `via ${via.name}: ${via.text}` : (w.text || '')).slice(0, 90),
    }
  }), [words, at])
  /* THE WORDS YOU AVOID, AS THE EDITOR CATCHES THEM (owner call 2026-09-30):
     your own, never the one being written (its page is where you say why),
     each with its reason and the words you would rather use — the kit paints
     them red and its catch card offers the swap */
  const avoidedTerms = useMemo(() => words.filter(w => avoided(w) && w.name.trim() && w.id !== at && !w.owner).map(w => ({
    id: w.id, slug: w.slug, term: w.name, aliases: elsewhere(w), stance: 'avoided',
    rather: w.rather || [], replacement: w.replacement || null, gloss: (w.text || '').slice(0, 160),
  })), [words, at])
  // THE ADDRESS IS AN ID. A slug changes with every keystroke of the title and
  // is never what a page is found by; an old link by slug resolves once and
  // the URL is rewritten to the id.
  // by id first; a slug from the address (or an old link) resolves once to the id
  // — among the rows the address's identity owns: /mara/tidepool is Mara's
  // tidepool even when you have defined one too, and /you/tidepool is yours
  // (a name is a friend's only when a shared row wears it — never a guess
  // against an identity that has not arrived yet, so your own address
  // resolves before /api/me answers, as it always has)
  const who = pathOf().who
  const friendly = !!who && words.some(w => w.owner && ownerSlug(w.owner) === who)
  const under = w => friendly ? !!w.owner && ownerSlug(w.owner) === who : !w.owner
  const current = (at && (words.find(w => w.id === at) || words.find(w => w.slug === at && under(w)) || (at === NEW ? words.find(w => !w.name.trim()) : null))) || null
  useEffect(() => {
    if (current && current.id !== at) setRoute(r => ({ ...r, page: 'words', at: current.id }))
  }, [current, at])
  // THE URL FOLLOWS THE WORD: as the title is typed the slug moves, and the
  // address moves with it — a reading, rewritten in place, never a navigation
  useEffect(() => {
    if (!current || !me) return
    const want = urlOf(current)
    if (window.location.pathname !== want) window.history.replaceState(null, '', want + window.location.hash)   // keep the shell's mobile hash (#menu, #wall/…)
  }, [current && current.slug, current && current.id, me && me.username]) // eslint-disable-line react-hooks/exhaustive-deps
  // a local word with no name yet is a page, not a row: it enters the list on its first letter
  /* …but a word LEFT untitled with a definition written is a row (audit
     2026-10-02): it stood on the server and nowhere on screen — not in the
     list, the atrium or Find — so the words were unreachable. It lists as
     "Untitled" (under #) once you are off its page, to be named or deleted. */
  const listed = useMemo(() => words.filter(w => w.name.trim() || (w.id !== current?.id && !isLocal(w.id) && !w.owner && (w.text || '').trim())), [words, current?.id])
  const shown = useMemo(() => filterWords(listed, { standing, tags: tagFilter, from: fromFilter }), [listed, standing, tagFilter, fromFilter])
  // the row being renamed in place (one at a time) and the name it began from; null when none
  const [renaming, setRenaming] = useState(null)
  /* A ROW BEING RENAMED HOLDS ITS PLACE (Finder's): its name reaches the page
     as it is typed, but the list files it by the name it began from until the
     rename ends, so the field never moves out from under the hand */
  const groups = useMemo(() => {
    if (!renaming) return byLetter(shown)
    const nameOf = w => (w.id === renaming.id ? renaming.from : w.name)
    return byLetter(bySort(shown, nameOf), nameOf)
  }, [shown, renaming])
  // the friends whose words are here, counted — the From lens's vocabulary
  const owners = useMemo(() => { const m = new Map(); for (const w of listed) if (w.owner) { const o = m.get(w.owner.user_id) || { ...w.owner, n: 0 }; o.n += 1; m.set(w.owner.user_id, o) } return [...m.values()].sort((a, b) => ownerSlug(a).localeCompare(ownerSlug(b))) }, [listed])
  // every tag and type the dictionary carries, counted — the filter's vocabulary
  /* THE DICTIONARIES ARE THE TAGS (owner call 2026-09-20): a shelf per tag
     over your own words, and a shelf minted before any word wears its tag is
     kept on this device until one does — the graph has no empty tag. */
  /* THE DICTIONARIES THEMSELVES (owner call 2026-10-01): a `lexicon` node per
     dictionary, on every device, empty or not, and whether it is an
     ANTI-DICTIONARY. Membership is still the tag: a word stands in every
     dictionary it is tagged with. Your dictionaries are the nodes and the tags
     your words wear; a tag with no node yet is an ordinary dictionary. */
  const [lexicons, setLexicons] = useState([])
  const loadLexicons = useCallback(async () => {
    try { const r = await api.lexicons(); setLexicons(r.lexicons || []); return r.lexicons || [] } catch { return null }
  }, [])
  // the dictionaries this device founded before they were nodes move into the graph, once
  const migrateEmpties = async have => {
    if (!have) return
    const read = k => { try { return JSON.parse(localStorage.getItem(k) || '[]') } catch { return [] } }
    const held = read('dictionary-shelves'), anti = read('dictionary-anti-shelves')
    if (!held.length && !anti.length) return
    for (const [names, isAnti] of [[held, false], [anti, true]]) {
      for (const n of names) if (n && !have.some(l => l.name === n)) await api.createLexicon({ name: n, anti: isAnti }).catch(() => {})
    }
    try { localStorage.removeItem('dictionary-shelves'); localStorage.removeItem('dictionary-anti-shelves') } catch { /* device only */ }
    loadLexicons()
  }
  const lexOf = t => lexicons.find(l => l.name === t)
  /* A TAG WITH NO NODE IS READ OFF ITS WORDS (audit 2026-10-02). The node says
     what kind a dictionary is; a tag that has none yet — worn before the
     lexicon nodes existed, or minted on another device or in another app —
     is an anti-dictionary when every word of yours wearing it is an anti-word
     (crux §8: "an anti-word's tags make anti-dictionaries"). Read as an
     ordinary dictionary it stood in the atrium as "0 words" (its words are
     all avoided) and entered onto an empty list. */
  const antiByWords = useMemo(() => {
    const seen = new Map()   // tag → every own word wearing it is avoided
    for (const w of listed) if (!w.owner) for (const t of (w.tags || [])) seen.set(t, (seen.has(t) ? seen.get(t) : true) && avoided(w))
    return seen
  }, [listed])
  const isAntiTag = t => { const l = lexOf(t); return l ? !!l.anti : !!antiByWords.get(t) }
  /* YOUR ORDER (owner ask 2026-10-03): the dictionaries stand in the order you dragged them
     into, the same on every device: each node's `order` (the shell's AtriumOrder). A move shows at once
     (`moving`, the ranks being written) and falls back if the write fails. */
  const [moving, setMoving] = useState(null)   // { tag: rank } while a move is being kept
  const shelves = useMemo(() => {
    const m = new Map(lexicons.map(l => [l.name, { tag: l.name, anti: l.anti, id: l.id, order: l.order, founded: l.founded, words: [] }]))
    for (const w of listed) if (!w.owner) for (const t of (w.tags || [])) {
      if (!m.has(t)) m.set(t, { tag: t, anti: !!antiByWords.get(t), id: null, words: [] })
      m.get(t).words.push(w)
      // a bare tag (no node) was founded with the first word that wore it
      const s = m.get(t)
      if (!s.id && w.createdAt && (!s.founded || w.createdAt < s.founded)) s.founded = w.createdAt
    }
    const all = [...m.values()].map(s => moving && s.tag in moving ? { ...s, order: moving[s.tag] } : s)
    // in your order; a bare tag (no founding known) keeps its place by name
    return AtriumOrder.sorted([...all].sort((a, b) => a.tag.localeCompare(b.tag)))
  }, [listed, lexicons, antiByWords, moving])
  /* THE ANTI-WORD IS INHERITED: a word wearing an anti-dictionary's tag is an
     anti-word. A tag change that brings one in makes the word an anti-word; one
     that takes the last one away makes it a word again; any other change leaves
     a word you marked by hand as you marked it. */
  const stanceFor = (w, tags, antiTag = isAntiTag) => inheritedStance(w, tags, antiTag)
  const editTags = (w, tags) => { const st = stanceFor(w, tags); store.edit(w.id, st ? { tags, stance: st } : { tags }) }
  const lens = tagFilter.length === 1 ? tagFilter[0] : ''   // the one dictionary in force, '' for the master dictionary
  const anti = standing === 'avoided'                       // …and whether it is an anti-dictionary
  // ON A PHONE the home is the split view's second page, shown only with a
  // word open — so the atrium asked for by name slides in as that page
  const [atriumOpen, setAtriumOpen] = useState(false)
  // THE ATRIUM REMEMBERS (owner call 2026-09-22): when each dictionary was
  // last entered or read, and the word last open in it — the atrium orders its
  // tiles by the first, and entering a dictionary lands on the second.
  // Keyed by tag, '' for the master dictionary. Device-only, like the shelves.
  const [visits, setVisits] = useState(() => { try { return JSON.parse(localStorage.getItem('dictionary-visits')) || {} } catch { return {} } })
  const visit = (tag, word) => setVisits(prev => {
    const next = { ...prev, [tag]: { at: Date.now(), word: word ?? prev[tag]?.word } }
    try { localStorage.setItem('dictionary-visits', JSON.stringify(next)) } catch { /* device only */ }
    return next
  })
  // reading a word counts as being in the dictionary in force
  const lensKey = tag => tag    // one dictionary, one memory: a tag is either kind, never both
  useEffect(() => { if (current && !current.owner) visit(lensKey(lens), current.id) }, [current?.id, lens])   // eslint-disable-line react-hooks/exhaustive-deps
  // the atrium stands whenever no word is open; the frame folds around it
  // itself (the shell's atriumStage: the list steps away, Focus withdraws).
  // YOUR OWN WORDS make the atrium (audit 2026-10-02): a friend's shared words
  // are in your list, never in your dictionaries.
  const ownWords = words.some(w => !w.owner)
  // AN EMPTY DICTIONARY IS STILL A SHELF (2026-10-05: a new account had no
  // atrium and the mark led nowhere): once the read has answered, the atrium
  // stands at zero with its plus; only a pending or failed read keeps it back
  const shelved = ownWords || (!loading && !error)
  // the node behind a dictionary, made when an act needs it
  // (`strict`: a failure throws to the caller, the move's picker, instead of the notice;
  // the answer says whether the dictionary already `existed`)
  const ensureLexicon = async (name, fields = {}, { strict = false } = {}) => {
    const have = lexOf(name)
    if (have) {
      if (!Object.keys(fields).length) return { ...have, existed: true }
      setLexicons(ls => ls.map(l => l.id === have.id ? { ...l, ...fields } : l))
      const r = await api.updateLexicon(have.id, fields).catch(e => { if (strict) throw e; setNotice(`could not change ${name}: ${e.message}`); return null })
      return r ? r.lexicon : have
    }
    const r = await api.createLexicon({ name, ...fields }).catch(e => { if (strict) throw e; setNotice(`could not make ${name}: ${e.message}`); return null })
    if (r) setLexicons(ls => ls.some(l => l.id === r.lexicon.id) ? ls : [...ls, r.lexicon])
    return r ? { ...r.lexicon, existed: !!r.existed } : null
  }
  // a dictionary's node retired, by the node in hand (one just founded is not in this render's list yet)
  const retireLexicon = l => { setLexicons(ls => ls.filter(x => x.id !== l.id)); api.updateLexicon(l.id, { trashed: true }).catch(() => {}) }
  const ownIn = tag => listed.filter(w => !w.owner && (w.tags || []).includes(tag))
  // deleting a dictionary takes its tag off every word (the words stay) and retires its node
  const deleteShelf = tag => {
    for (const w of ownIn(tag)) editTags(w, w.tags.filter(t => t !== tag))
    const l = lexOf(tag)
    if (l) { setLexicons(ls => ls.filter(x => x.id !== l.id)); api.updateLexicon(l.id, { trashed: true }).catch(() => {}) }
    setTagFilter(f => f.filter(t => t !== tag))
  }
  // RENAMING A DICTIONARY (owner call 2026-09-24): its name moves on every one of
  // your words that wears it (into an existing name, the two merge), on its node,
  // and on everything that remembers it by name — the lens, the atrium's order
  const renameShelf = (from, to) => {
    const t = (to || '').trim()
    if (!t || t === from) return
    const l = lexOf(from)
    if (l && !lexOf(t)) { setLexicons(ls => ls.map(x => x.id === l.id ? { ...x, name: t } : x)); api.updateLexicon(l.id, { name: t }).catch(e => setNotice(e.message)) }
    for (const w of ownIn(from)) store.edit(w.id, { tags: [...new Set(w.tags.map(x => x === from ? t : x))] })
    setTagFilter(f => f.includes(from) ? [...new Set(f.map(x => x === from ? t : x))] : f)
    setVisits(prev => { if (!prev[from]) return prev; const { [from]: moved, ...rest } = prev; const next = { ...rest, [t]: moved }; try { localStorage.setItem('dictionary-visits', JSON.stringify(next)) } catch { /* device only */ } return next })
  }
  // founding one makes its node at once, so it stands on every device before any word wears it
  const mintShelf = name => { mintCollection(name) }
  /* MINT A COLLECTION (the atrium's maker; the drawer's "＋ New dictionary" when the shell's
     drawer has one): founded through the move's createDictionary — a name that stands, in any
     case, is that dictionary — and made the one in force. It takes no rank, so it stands last. */
  const mintCollection = async raw => {
    const name = String(raw || '').trim()
    if (!name) return null
    try {
      const tag = await mover.createDictionary(name)
      setStanding(isAntiTag(tag) ? 'avoided' : 'all'); setTagFilter([tag]); setAtriumOpen(false)
      return tag
    } catch (e) { setNotice(e.message); return null }
  }
  /* THE ATRIUM'S DRAG (the kit's onReorder): the new order of the dictionaries' tags, shown at
     once, kept on their nodes (one write for one move; a tag with no node yet gets one), and put
     back if the graph refuses — the answer, false, puts the tiles back too. */
  const reorderShelves = async ids => {
    const ranks = Object.fromEntries(shelves.map(s => [s.tag, s.order]))
    const kept = new Map()
    const write = async (tag, order) => {
      const l = lexicons.find(x => x.name === tag) || kept.get(tag)
      const r = l ? await api.updateLexicon(l.id, { order }) : await api.createLexicon({ name: tag, anti: isAntiTag(tag), order })
      kept.set(tag, r.lexicon)
    }
    const done = await AtriumOrder.commit(ids, ranks, { write,
      show: writes => setMoving(m => ({ ...m, ...Object.fromEntries(writes.map(w => [w.id, w.order])) })) })
    if (done.ok) setLexicons(ls => [...ls.map(l => kept.get(l.name) || l), ...[...kept.values()].filter(k => !ls.some(l => l.id === k.id))])
    else setNotice(`could not keep that order: ${done.error?.message || 'the graph refused it'}`)
    setMoving(null)
    return done.ok
  }
  // SET AS ANTI-DICTIONARY (owner call 2026-10-01): its words inherit the anti-word stance;
  // set back, the ones with no other anti-dictionary are words again
  const setAntiShelf = async (tag, on) => {
    await ensureLexicon(tag, { anti: on })
    const antiTag = t => t === tag ? on : isAntiTag(t)
    for (const w of ownIn(tag)) {
      if (on && !avoided(w)) store.edit(w.id, { stance: 'avoided' })
      if (!on && avoided(w) && !(w.tags || []).some(t => t !== tag && antiTag(t))) store.edit(w.id, { stance: 'held' })
    }
    if (lens === tag) setStanding(on ? 'avoided' : 'all')
    setNotice(on ? `“${tag}” is an anti-dictionary: its words are anti-words now.` : `“${tag}” is a dictionary again.`)
  }
  // DUPLICATE: a second dictionary of the same kind, worn by the same words
  const duplicateShelf = async tag => {
    const taken = new Set(shelves.map(s => s.tag.toLowerCase()))
    let name = `${tag} copy`
    for (let n = 2; taken.has(name.toLowerCase()); n++) name = `${tag} copy ${n}`
    await ensureLexicon(name, { anti: isAntiTag(tag) })
    for (const w of ownIn(tag)) store.edit(w.id, { tags: [...w.tags, name] })
    setNotice(`“${name}” is a copy of “${tag}”.`)
  }
  /* MOVING WORDS BETWEEN DICTIONARIES (owner ask 2026-10-03): moveEntries,
     createDictionary and moveIntoNew, out of the dictionary in force; see moving.js */
  const mover = makeMover({ store, isAntiTag, ensureLexicon, retireLexicon, lens })
  const exportShelf = (tag, how) => { const ws = ownIn(tag); if (how === 'md') downloadMarkdownFiles(ws); else downloadJson(ws) }
  // the filter's tags are the tags of the stance in force
  const tagCounts = useMemo(() => { const m = new Map(); for (const w of listed) if (avoided(w) === anti) for (const t of (w.tags || [])) m.set(t, (m.get(t) || 0) + 1); return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0])) }, [listed, anti])
  const letters = useMemo(() => groups.map(g => g.letter), [groups])
  const allShut = letters.length > 0 && letters.every(l => shutLetters.has(l))
  const counts = useMemo(() => {
    const held = listed.filter(w => !avoided(w))
    return {
      all: held.length,
      committed: held.filter(w => w.status === 'committed').length,
      proposed: held.filter(w => w.status === 'proposed').length,
      shared: held.filter(w => w.owner).length,
      avoided: listed.filter(avoided).length,
    }
  }, [listed])

  /* THE SELECTION (owner ask 2026-10-03, the shell's useListSelection): Finder's
     gestures over the list — ⌘-click toggles, ⇧-click takes the run, a press in
     empty space draws the marquee, ⌘A / Esc / ⇧⌘M while the list has the
     keyboard, Return renames the one row chosen (your own word; a friend's is
     theirs to name), and a selected row drags the whole selection. The rows in the
     order shown (a shut letter's are not shown); the open word joins a ⌘-click. */
  const rowIds = useMemo(() => groups.filter(g => !shutLetters.has(g.letter)).flatMap(g => g.words.map(w => w.id)), [groups, shutLetters])
  const sel = useListSelection({ ids: rowIds, current: current ? current.id : null,
    name: id => (words.find(w => w.id === id) || {}).name || 'Untitled',
    onMove: (ids, anchor) => askMove(ids, anchor),
    onRename: id => { const w = words.find(x => x.id === id); if (w && !isLocal(id) && !w.owner) startRename(w) } })
  const marked = sel.selected
  // BACKSPACE AND DELETE REMOVE WHAT IS SELECTED — but only when the hands are
  // not in a field: the editor, a title, a search line and a menu all own those
  // keys first, and a stray Backspace must never eat the words behind them.
  const deleteSelected = () => {
    // a friend's word is selected like any row and removed like none
    const set = words.filter(w => marked.has(w.id) && !isLocal(w.id) && !w.owner)
    for (const w of set) { store.remove(w.id); if (w.id === at) go('words') }
    sel.clear()
    return set.length
  }
  useEffect(() => {
    if (!marked.size) return undefined
    const onKey = e => {
      if (e.key !== 'Backspace' && e.key !== 'Delete') return
      const t = document.activeElement
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return
      if (document.querySelector('[role="menu"], [role="dialog"]')) return
      if (!words.some(w => marked.has(w.id) && !isLocal(w.id) && !w.owner)) return
      e.preventDefault()
      deleteSelected()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [marked, words, at]) // eslint-disable-line react-hooks/exhaustive-deps

  /* MOVE TO… (owner ask 2026-10-03): the shell's move (useMoveFlow) over this
     space's dictionaries, opened by the selection bar, a row's menu or ⇧⌘M, and
     taking words dropped on a dictionary. The shell folds the leaving rows,
     runs the write, says it with Undo and unfolds them on a failure; what a
     move means and its undo are moving.js's. Only your own saved words move;
     a friend's stays theirs. */
  // THE DICTIONARIES' DRAWER at the top of the list (the shell's CollectionDrawer, the Mac's
  // CollectionHead / CollectionDrawer): the dictionary you're in, and every one beneath it, a
  // place to enter and a place carried words land. Remembered on this device.
  const [shelfOpen, setShelfOpen] = useStored('dictionary:shelf-drawer', false)
  const movable = ids => ids.filter(id => { const w = words.find(x => x.id === id); return w && !w.owner && !isLocal(id) })
  const rooms = useMemo(() => shelves.map(s => ({ id: s.tag, name: s.tag, badge: s.anti ? 'Anti' : undefined,
    count: s.anti ? s.words.length : s.words.filter(w => !avoided(w)).length })), [shelves])
  /* THE THREE VERBS (owner call 2026-10-04, as people.oS): MOVE TO… (out of the dictionary
     in force, into the target), ADD TO… (into the target, out of nothing; the shell's second
     flow, worded Add) and REMOVE FROM <dictionary> (out of the one in force, into every word,
     where every word stays; hidden in every word). Each is moving.js's write and its undo; a
     drop on a dictionary stays a move. */
  // a REMOVE FROM's target is { from }: into every word (MASTER, '')
  const toTag = target => (target.create != null ? target.create : target.to == null ? '' : typeof target.to === 'string' ? target.to : target.to.id)
  // the rows that leave the list in force: those the filter no longer shows once moved (or added)
  const leavingFrom = from => (ids, target) => {
    const after = mover.predict(ids, toTag(target), { from })
    const stays = new Set(filterWords(after.filter(w => ids.includes(w.id)), { standing, tags: tagFilter, from: fromFilter }).map(w => w.id))
    return ids.filter(id => !stays.has(id))
  }
  const settle = r => {
    if (!r.moved.length && !r.unchanged.length) throw new Error(r.failed[0]?.error || 'these words cannot move')
    return {
      count: r.moved.length + r.unchanged.length, ...(r.to ? { to: r.to } : {}), failed: r.failed,
      undo: r.moved.length ? async () => {
        const u = await r.undo()
        if (u.failed.length) throw new Error(`${countOf(u.failed.length, 'word')} could not be put back: ${u.failed[0].error}`)
      } : null,
    }
  }
  const flow = useMoveFlow({
    selection: sel, rooms, currentId: lens || null, noun: 'word', kind: 'dictionary',
    leaving: leavingFrom(lens),
    move: async (ids, target) => {
      const these = movable(ids)
      return settle(target.create != null ? await mover.moveIntoNew(target.create, these) : await mover.moveEntries(these, toTag(target)))
    },
    // REMOVE FROM <the dictionary in force>: out of it, into every word (the shell says
    // "Removed 2 words from spanish", with Undo)
    remove: async (ids, from) => settle(await mover.removeEntries(movable(ids), { from: from.id })),
  })
  const addFlow = useMoveFlow({
    selection: sel, rooms, currentId: null, noun: 'word', kind: 'dictionary', verb: 'Add',
    leaving: leavingFrom(''),
    move: async (ids, target) => {
      const these = movable(ids)
      return settle(target.create != null ? await mover.addIntoNew(target.create, these) : await mover.addEntries(these, toTag(target)))
    },
  })
  // only your own saved words move: a friend's word is never handed to the move
  const ownOrSay = ids => {
    const these = movable(ids)
    if (!these.length) setNotice('a word shared with you stays in its owner’s dictionary')
    return these
  }
  const askMove = (ids, anchor) => { const these = ownOrSay(ids); if (these.length) flow.ask(these, anchor) }
  const askAdd = (ids, anchor) => { const these = ownOrSay(ids); if (these.length) addFlow.ask(these, anchor) }
  const removeFromLens = ids => { const these = ownOrSay(ids); if (these.length) flow.remove(these) }
  const dropWords = (tag, ids) => flow.drop(tag, movable(ids))
  const rowRect = id => (document.querySelector(`.dt-list__body [data-id="${CSS.escape(id)}"]`) || document.querySelector('.dt-list__body'))?.getBoundingClientRect()

  // EVERY VERB GOES THROUGH THE STORE: local first, the wire after, ids stable.
  // a tag change on the page carries the stance it implies (the anti-word is inherited)
  /* A NAME IS UNIQUE IN ITS DICTIONARY (crux §8), and a title is typed, not
     created (audit 2026-10-02: + then "drift" made a second drift). A word is
     born untitled and named by updates, which the create's 409 never sees — so
     a typed name another of your words already holds is not sent. The page
     keeps it on screen and says so; the next keystroke that makes it unique
     ("drift" → "driftwood") goes out as usual. */
  const clashSaid = useRef('')
  // another of your own words already holds this name
  const taken = (id, name) => {
    const n = String(name || '').trim().toLowerCase()
    return !!n && words.some(w => w.id !== id && !w.owner && w.name.trim().toLowerCase() === n)
  }
  const patch = fields => {
    if (!current) return
    if ('name' in fields && !current.owner) {
      if (taken(current.id, fields.name)) {
        const { name, ...rest } = fields
        clashSaid.current = `“${name.trim()}” is already in the dictionary — this word keeps its old name until the new one differs`
        setNotice(clashSaid.current)
        fields = rest
        if (!Object.keys(fields).length) return
      } else if (clashSaid.current) { const said = clashSaid.current; clashSaid.current = ''; setNotice(m => (m === said ? '' : m)) }
    }
    if (Array.isArray(fields.tags) && !current.owner) { const st = stanceFor(current, fields.tags); if (st && !('stance' in fields)) fields = { ...fields, stance: st } }
    store.edit(current.id, fields)
  }
  const commit = () => { if (current) store.commit(current.id) }
  // THE STAMP (owner call 2026-09-19): a member's commitment to the node as it
  // stands — the author included, after writing, like anyone
  const stamp = () => { if (current) store.stamp(current.id) }
  const unstamp = () => { if (current) store.unstamp(current.id) }
  const remove = () => { if (current) { store.remove(current.id); go('words') } }
  const linkOf = w => `${location.origin}${urlOf(w)}`
  const removeWord = w => { store.remove(w.id); if (w.id === at) go('words') }
  // a duplicate that fails SAYS SO where the hand is (the notice under the list
  // head), not only in the banner — which on a revoked session already read the
  // same red line, so a press that did nothing looked like a button that did nothing
  const duplicateWord = async w => {
    try { const d = await store.duplicate(w); if (d) { go('words', d.id); setNotice(`${d.name} is in the dictionary.`) } }
    catch (e) { setNotice(`could not duplicate ${w.name} — ${e.message}`) }
  }
  const [shareWord, setShareWord] = useState(null)
  const [exportWord, setExportWord] = useState(null)
  const startRename = w => setRenaming({ id: w.id, from: w.name })
  /* THE RENAME IS TETHERED TO THE OPEN PAGE: each keystroke in the row is the
     page's own title edit — the store, the heading, the coalesced save — save
     an empty name or one another word holds, which wait unwritten and unsaid.
     The name is judged when the rename ends: Esc, an emptied field or a name
     already in the dictionary put back the one it began from, in both places. */
  const renameInput = (w, name) => { if (w.id === current?.id && name.trim() && !taken(w.id, name)) store.edit(w.id, { name }) }
  const renameWord = (w, name) => {
    const { from } = renaming
    setRenaming(null)
    if (name && taken(w.id, name)) setNotice(`"${name}" is already in the dictionary`)
    const keep = name && !taken(w.id, name) ? name : from
    if (keep !== words.find(x => x.id === w.id)?.name) store.edit(w.id, { name: keep })
  }
  // THE MENU ACTS ON WHAT IS MARKED. Right-clicking a marked row and getting a
  // verb that touched only that row is the multi-select doing nothing — the
  // whole point of marking is acting on the set at once. A row OUTSIDE the
  // mark still acts on itself, and taking a verb clears the mark, because the
  // words it named are gone or changed.
  const batch = w => (marked.size > 1 && marked.has(w.id))
    ? words.filter(x => marked.has(x.id) && !isLocal(x.id) && !x.owner)
    : null
  const runMany = async (set, fn) => { for (const x of set) { try { await fn(x) } catch (e) { console.warn('[batch]', x.name, e.message) } } sel.clear() }
  // A FRIEND'S WORD, COPIED INTO YOUR DICTIONARY (owner call 2026-09-19): the
  // one verb a shared row carries besides its link — the same word, now in
  // your terms, by the door every word of yours is minted through
  // A WORD TRAVELS AS A COPY (owner call 2026-09-19): into one of your groups,
  // or out of a group into your own dictionary. The target is another
  // dictionary, so the list here only moves when the copy landed in the
  // space you are looking at; otherwise the notice says where it went.
  // The copy carries WHERE IT CAME FROM — your own dictionary, this group's,
  // or a friend's — for the ledger's first entry.
  const fieldsOf = w => ({ name: w.name, body: w.body || '', text: w.text || '', dkind: w.dkind || '', tags: w.tags || [], aliases: w.aliases || [], properties: w.properties || [], property_order: w.property_order || [],
    source: w.owner ? { kind: 'friend', name: w.owner.display_name || w.owner.username || '' } : activeGroup ? { kind: 'group', name: activeGroup.name } : { kind: 'you' } })
  const copyInto = async (w, into, label) => {
    try {
      const r = await api.copyInto(into, fieldsOf(w))
      if (into === (me?.groupId || 'me')) await load()
      setNotice(`${w.name} is in ${label}.`)
      return { outcome: 'copied', word: r.word }
    } catch (e) {
      setNotice(e.status === 409 ? `${w.name} is already in ${label}.` : `could not copy ${w.name}: ${e.message}`)
      return { outcome: e.status === 409 ? 'there' : 'failed', word: null }
    }
  }
  // a friend's word copied into your own dictionary opens as yours; a group's
  // word goes to your dictionary, which is elsewhere — the notice says so
  const keepWord = async w => { const r = await copyInto(w, 'me', 'your dictionary'); if (r.word && !activeGroup) go('words', r.word.id) }
  const keep = keepWord

  // a friend's word wears three verbs: its link, a share (to your groups —
  // the sheet knows the word is not yours to send to a person), and a copy
  // into your own dictionary — never rename, commit or remove, which are the owner's
  const rowItems = w => isLocal(w.id) ? [] : w.owner ? [
    { icon: <LucideGlyph name="share" />, label: 'Share', onSelect: () => setShareWord(w) },
    { icon: <LinkIcon size={16} />, label: 'Copy link', onSelect: () => navigator.clipboard?.writeText(rowAct.current.linkOf(w)).catch(() => {}) },
    { separator: true },
    { icon: <CopyIcon size={16} />, label: 'Copy into my dictionary', onSelect: () => rowAct.current.keepWord(w) },
  ] : [
    // the field opens after the menu has closed and given focus back, or
    // Radix's close-autofocus lands on the trigger and blurs the field at once
    { icon: <PenIcon size={16} />, label: 'Rename', onSelect: () => setTimeout(() => startRename(w), 60) },
    ...(w.legacy ? [] : [{ icon: <LucideGlyph name="share" />, label: 'Share', onSelect: () => setShareWord(w) }]),
    { icon: <LinkIcon size={16} />, label: 'Copy link', onSelect: () => navigator.clipboard?.writeText(rowAct.current.linkOf(w)).catch(() => {}) },
    // in a group's dictionary, the word is the group's: a copy of it can be yours
    ...(activeGroup ? [{ icon: <CopyIcon size={16} />, label: 'Copy into my dictionary', onSelect: () => rowAct.current.copyInto(w, 'me', 'your dictionary') }] : []),
    // MOVE TO… (owner ask 2026-10-03): the shell's picker, for the selection this row is in, else the row
    ...(w.legacy ? [] : [{ icon: <LucideGlyph name="move" />, label: batch(w) ? `Move ${batch(w).length} words to…` : 'Move to…', hint: '⇧⌘M',
      onSelect: () => { const L = rowAct.current; L.askMove(L.sel.idsFor(w.id), L.rowRect(w.id)) } },
      // ADD TO… (2026-10-04): into another dictionary as well; every one it is in keeps it
      { icon: <PlusIcon size={16} />, label: batch(w) ? `Add ${batch(w).length} words to…` : 'Add to…',
        onSelect: () => { const L = rowAct.current; L.askAdd(L.sel.idsFor(w.id), L.rowRect(w.id)) } },
      // REMOVE FROM <the dictionary in force>: out of it; the word stays in every word (hidden there)
      ...(lens ? [{ icon: <CloseIcon size={16} />, label: batch(w) ? `Remove ${batch(w).length} words from “${lens}”` : `Remove from “${lens}”`,
        onSelect: () => { const L = rowAct.current; L.removeFromLens(L.sel.idsFor(w.id)) } }] : [])]),
    { separator: true },
    { icon: <LucideGlyph name="duplicate" />, label: batch(w) ? `Duplicate ${batch(w).length} words` : 'Duplicate',
      onSelect: () => { const L = rowAct.current; L.batch(w) ? L.runMany(L.batch(w), x => L.store.duplicate(x)) : L.duplicateWord(w) } },
    { icon: <LucideGlyph name="export" />, label: 'Export…', onSelect: () => setExportWord(w) },
    // no uncommit (owner call 2026-09-18): a committed word is deleted or it stands — that is the intentionality
    ...(w.legacy || w.status === 'committed' ? []
      : [{ icon: <CommitIcon size={16} />, label: batch(w) ? `Commit ${batch(w).length} words` : 'Commit',
          onSelect: () => { const L = rowAct.current; L.batch(w) ? L.runMany(L.batch(w), x => L.store.commit(x.id)) : L.store.commit(w.id) } }]),
    { separator: true },
    { icon: <LucideGlyph name="trash" />, label: batch(w) ? `Delete ${batch(w).length} words` : 'Delete',
      danger: true,
      onSelect: () => { const L = rowAct.current; L.batch(w) ? L.runMany(L.batch(w), x => { L.store.remove(x.id); if (x.id === L.at) L.go('words') }) : L.removeWord(w) } },
  ]
  /* THE ROWS' LIVE HANDLE (see WordRow): refreshed on every render, read by a
     row when it renders and by a verb when it is pressed */
  const rowAct = useRef(null)
  rowAct.current = { rowItems, go, renameWord, renameInput, linkOf, keepWord, copyInto, batch, runMany, duplicateWord, removeWord, store, at, mover, sel, askMove, askAdd, removeFromLens, rowRect }
  // THE + OPENS A NEW WORD'S PAGE WITH THE CURSOR IN THE TITLE. It is a word
  // from its first moment — a local one, addressed by its own id, in the list
  // from its first letter; the graph learns it once it has a name.
  // inside an anti-dictionary a new word is born avoided, wearing its tag
  const fresh = () => { const id = store.create(anti ? { stance: 'avoided', tags: lens ? [lens] : [] } : {}); go('words', id) }
  const openById = id => { if (words.some(x => x.id === id)) go('words', id) }
  // ENTERING A DICTIONARY from the atrium: the lens goes on and the page is
  // its last-read word, else its latest-edited one; an empty one opens a new
  // word already wearing it
  const edited = w => w.editedAt || w.updatedAt || w.createdAt || ''
  // an ANTI-DICTIONARY is entered the same way, under the Anti-words standing
  const enter = tag => {
    const isAnti = !!tag && isAntiTag(tag)
    setTagFilter(tag ? [tag] : []); setAtriumOpen(false)
    setStanding(isAnti ? 'avoided' : 'all')
    const key = lensKey(tag)
    const pool = listed.filter(w => !w.owner && w.name.trim() && avoided(w) === isAnti && (!tag || (w.tags || []).includes(tag)))
    const target = pool.find(w => w.id === visits[key]?.word) || [...pool].sort((a, b) => String(edited(b)).localeCompare(String(edited(a))))[0]
    visit(key, target?.id)
    if (target) return go('words', target.id)
    const id = store.create({ ...(tag ? { tags: [tag] } : {}), ...(isAnti ? { stance: 'avoided' } : {}) })
    go('words', id)
  }
  // a slug names your own word first — the marks and the tip only ever offer yours
  const openBySlug = slug => { const w = words.find(x => x.slug === slug && !x.owner) || words.find(x => x.slug === slug); if (w) go('words', w.id) }

  const logout = async () => { await api.logout().catch(() => {}); window.location.assign('/') }
  const railGroups = spaces.groups.map(g => ({ id: g.id, name: g.name, avatarUrl: g.avatarUrl || null, avatar_url: g.avatarUrl || null }))
  const identity = {
    currentUser: { id: me?.user_id, username: me?.username || 'You', avatarUrl: me?.avatarUrl || null },
    groups: railGroups,
    activeGroup: activeGroup ? railGroups.find(g => g.id === activeGroup.id) || { id: activeGroup.id, name: activeGroup.name } : null,
    groupsAuth: Object.fromEntries(spaces.groups.map(g => [g.id, g.authorized !== false])),
    onSwitchSpace: space => switchSpace(space ? spaces.groups.find(g => g.id === space.id) || space : null),
    spaceHref: space => `/${encodeURIComponent(space ? (spaces.groups.find(g => g.id === space.id)?.slug || space.id) : (me?.username || 'me'))}`,
    onConnectSpace: space => { if (space?.id) window.location.href = `/auth/connect-group?group_id=${encodeURIComponent(space.id)}` },
    onSignOut: logout,
  }

  if (me && !me.authorized) return <Door />
  // NO GATE ON IDENTITY (owner report 2026-09-19: "why does the app flicker
  // when refreshing? no other app does this"). This used to return a bare
  // shell — no rail, an empty stage — until /api/me answered, then swap to
  // the whole frame: a layout jump on every refresh. The frame, the rail and
  // the shelved list render at once now; identity fills its seats when it
  // arrives, and nothing below reads `me` without asking whether it is here.

  // THE FILTER'S ITEMS (owner call 2026-09-22): the head's MENU offers them
  // beside New and Search — the head's and the folded spine's alike.
  // THE MARK SITS AT THE ROW'S EDGE (owner call 2026-09-19: "the checkmarks
  // should be on the right, not left"): label, then the count in the
  // hotkey's faint mono, then the check — the ⋯ menu's own row grammar
  const filterItems = [
    ...STANDINGS.map(([id, label]) => ({
      label, count: counts[id], selected: standing === id,
      onSelect: () => setStanding(id),
    })),
    { separator: true },
    { icon: <TagIcon size={16} />, label: tagFilter.length ? `Tags · ${tagFilter.length}` : 'Tags', items: tagCounts.length
      ? [
        ...(tagFilter.length ? [{ label: 'Any tag', onSelect: () => setTagFilter([]) }, { separator: true }] : []),
        ...tagCounts.map(([t, n]) => ({ label: t, count: n, selected: tagFilter.includes(t),
          onSelect: () => setTagFilter(f => f.includes(t) ? f.filter(x => x !== t) : [...f, t]) })),
      ]
      : [{ label: 'No tags yet — add one on a word', disabled: true }] },
    // WHOSE (2026-09-19): yours, or one friend's — the lens stands only
    // once a friend has shared something, in the same row grammar
    ...(owners.length ? [{ icon: <PeopleIcon size={16} />, label: fromFilter ? `From · ${fromFilter === 'you' ? 'you' : ownerSlug(owners.find(o => o.user_id === fromFilter))}` : 'From', items: [
      { label: 'Anyone', count: counts.all, selected: !fromFilter, onSelect: () => setFromFilter('') },
      { label: 'You', count: counts.all - counts.shared, selected: fromFilter === 'you', onSelect: () => setFromFilter('you') },
      ...owners.map(o => ({ label: ownerSlug(o), count: o.n, selected: fromFilter === o.user_id, onSelect: () => setFromFilter(o.user_id) })),
    ] }] : []),
  ]
  /* THE LIST'S VERBS, ONCE (owner call 2026-09-22): the open head spreads
     them as word blocks and the folded spine's MENU is the kit's <PanelMenu>
     over THIS one list, so the two cannot drift. */
  const listItems = [
    // glyphs: what each word becomes when the column is too narrow for it
    // qhBody: what the Help card says over each verb
    { label: 'New word', glyph: <PlusIcon />, onSelect: fresh,
      qhBody: 'Starts a new word and opens its page: the first line is the word, everything under it the definition.' },
    // THE FILTER IS A VERB OF THE LIST (owner call 2026-09-22), so
    // it lives in MENU beside New, not in the title's menu of dictionaries
    { label: 'Filter', glyph: <FilterIcon />, on: filtered, items: filterItems,
      qhBody: 'Narrows the list: every word, committed, offered or shared with you, by tag, or by who it came from. It is lit while a filter is on.' },
    { label: allShut ? 'Expand all' : 'Collapse all', glyph: allShut ? <ExpandAllIcon /> : <CollapseAllIcon />, onSelect: () => setAllLetters(!allShut),
      qhBody: allShut
        ? 'Opens every letter of the list again, so all the words show.'
        : 'Folds every letter of the list shut, leaving just the letters. Press again to open them all.' },
  ]

  return <ShellProvider value={identity}>
    {/* THE SPLASH, BACK (owner call 2026-09-19), mounted as nucleus mounts it:
        the register's mark draws over the pre-paint ground and fades to the
        app. It waits on nothing now — identity no longer gates the frame, the
        list paints from its shelf, the fonts are preloaded — so the app is
        standing behind it by the time the mark has drawn. */}
    <Splash app="dictionary" grid={false} />
    {/* ONE RAIL (owner call 2026-09-21, the whiteboard — this app is the
        guinea pig for the shell's shape). The right upright is gone: its
        readings, the preferences, the fleet and the ways out live in the
        PLATFORM CONSOLE under the avatar (kit 0.253.0), because they matter
        and are not pressed often — importance and prominence are two axes.
        The two high-frequency ecosystem acts stay one click away on the left:
        PERSONAS (features.personas) and FOCUS (focus/onFocusChange; the seat
        stays standing while the rail slides out). Apps leaves the rail
        (features.launcher false) and the console carries it. The rail stays
        MOUNTED in focus (owner report 2026-09-19): setFocusMode slides it;
        rail={!zen} would unmount it before it could move. */}
    {/* THE APP RAIL, AS INK'S AND THE MAC'S (owner calls 2026-09-22,
        2026-10-12): the dictionary's mark (home, lit while you are here), the
        agent on the kit's Wings seat, Search, and the avatar in the last equal
        block. Preferences and Help left the rail: the avatar's dock carries
        both (its Settings page holds the dictionary's panes, its foot the
        Help door). The list's MENU keeps only the list's own verbs. */}
    <Shell currentApp="dictionary" bareStage onHome={() => go(HOME)}
      onSearch={() => setFindOpen(v => !v)}
      settings={DICT_SETTINGS}
      rail railItems={RAIL} dock
      // the lit seat is the page that stands: Search while find is up, Help on
      // the manual (the app's mark goes dark) — owner call 2026-09-23
      activeSeat={findOpen ? 'search' : page === 'help' ? 'help' : undefined}
      // Quick Help's docs door opens the manual in place — no reload, no splash
      onHelpPage={() => go('help')}
      focus={zen} onFocusChange={setZen}>

      {/* ONE PAGE MEANS NO PAGES COLUMN. With a single destination the kit's
          nav column would be a column of one, so the workspace IS the list and
          the word beside it — nothing stands between them and the rail. */}
      {/* DROP A DOCUMENT OR A LINK ANYWHERE (owner call 2026-09-18): the
          Lexicographer reads it and proposes the words it leans on — the same
          pass as Absorb, over a file or a page instead of an app */}
      {dropping && <div className="dt-drop-veil ofself-float-plate" aria-hidden="true"><span>Drop it — the Lexicographer reads it and proposes the words</span></div>}
      {/* THE MANUAL (2026-09-20): /help is a page of the app, in the stage
          beside the rail — the Quick Help card's docs door and the phone
          menu's Help row both land here. It stands in THIS workspace (owner
          call 2026-09-23: "help should use the same component"): the list's
          own column — its width, fold and seam — carries the manual's index,
          and the word's place carries the manual. */}
      <SplitView className="dt-workspace" storageKey="dictionary-list-width"
          /* ON A PHONE (2026-09-20): the list is page 0, the open word page 1;
             the kit slides between them and floats the way back top-left —
             from the word to the list here, from the list to the menu itself */
          mobileIndex={at || atriumOpen ? 1 : 0} onMobileBack={() => { setAtriumOpen(false); go(HOME) }}
          /* the phone scrolls the document, page by page: a new word opens at the top */
          mobileKey={current ? current._page || current.id : atriumOpen ? 'atrium' : null}
          leadingClassName="dt-list-panel" leadingProps={{ 'data-drawer-collapsed': folded || undefined,
            ...qh('The dictionary', 'Every word you have defined, grouped by first letter. Click a word to read it; drag the seam to resize the list, or fold it away with the collapse control.') }}
          leading={<>
            {/* on /help the same fold carries the manual's entries, and the
                list's tools stand down */}
            <PanelDrawer collapsed={folded} onToggle={fold} label={onHelp ? (help.activeLabel || 'Help') : 'Dictionary'} waterfall
              tools={onHelp ? undefined : <PanelMenu items={listItems} side="right" />}
              /* the folded spine's names are built only while it is folded — open,
                 the spine is not drawn, and 1,500 of them re-rendered on every
                 keystroke for nothing (audit 2026-10-02) */
              items={onHelp ? help.tiles : folded ? shown.map(w => ({ id: w.id, label: w.name || 'Untitled', active: w.id === at })) : undefined}
              onSelect={onHelp ? (id => { help.jump(id); fold(false) }) : (id => { go('words', id); fold(false) })} />
            {onHelp ? help.index : <LibraryPanel className="dt-list" head={<>
              {/* THE SPREAD HEAD (owner call 2026-09-22): no title — home on the
                  rail is the way to the atrium and the URL names the dictionary
                  in force — and no MENU: the list's verbs stand as word blocks,
                  divided by block seams (the kit's <PanelHeader spread>). */}
              <PanelHeader className="dt-list__brand" spread items={listItems} />
              {/* THE CHIP ROW: only while a filter narrows the list or a notice
                  stands — the tools themselves live in the brand head now */}
              {(filtered || notice) && <div className="dt-list__head dt-list__head--chips">
                {/* the filter says so in words when it is narrowing, because an
                    icon alone cannot tell you a list is incomplete */}
                {filtered && <button type="button" className="dt-narrowed" onClick={clearFilter} title="Clear the filter"
                  {...qh('A filter is on', 'The list is showing a subset — this says which one, and how many of the whole. Click it to clear the filter and see every word.')}>
                  {[standing !== 'all' && STANDINGS.find(([id]) => id === standing)[1], ...(tagFilter.length > 1 ? tagFilter.map(t => `#${t}`) : []),
                    fromFilter && (fromFilter === 'you' ? 'yours' : `from ${ownerSlug(owners.find(o => o.user_id === fromFilter))}`)].filter(Boolean).join(' · ')} · {shown.length} of {lens ? listed.filter(w => (w.tags || []).includes(lens)).length : listed.length}
                </button>}
                {notice && <button type="button" className="dt-narrowed dt-notice" onClick={() => setNotice('')}
                  {...qh('What just happened', 'A note on the last thing the dictionary did — words proposed from a drop, a word kept, a read that failed. Click to dismiss.')}>{notice}</button>}
              </div>}
            </>} foot={<>
              {/* MOVE TO…, ADD TO…, REMOVE FROM <the dictionary in force> (the shell's, from the
                  flows; each handed only your own words), DELETE, CLEAR: what does not fit folds into ⋯ */}
              <SelectionBar count={sel.count} noun="word" onClear={sel.clear}
                move={[{ ...flow, ask: (ids, anchor) => askMove(ids ?? sel.chosen(), anchor), remove: flow.remove && (ids => removeFromLens(ids ?? sel.chosen())) },
                  { ...addFlow, ask: (ids, anchor) => askAdd(ids ?? sel.chosen(), anchor) }]}
                actions={[{ label: 'Delete', danger: true, onSelect: deleteSelected, hint: 'Delete the selected words (⌫)' }]} /></>
            } bodyProps={{ className: `dt-list__body ofself-marquee-host${sel.marquee.marqueeing ? ' is-marqueeing' : ''}`,
                ...sel.listProps,
                ...qh('The words', 'Grouped by first letter; dim ones are offered and not yet committed. Press in empty space and drag to mark several — a right-click then acts on all of them, and Backspace or Delete removes them. Drop a file or a link anywhere to have the Lexicographer propose the words it leans on.') }}>
                {/* THE DICTIONARIES (owner ask 2026-10-03, so drag-to-move has somewhere to land on
                    the web as on the Mac): every word (which takes no drop), each dictionary
                    (the one you're in refuses), then the atrium */}
                {/* the rows stand in your order; the kit's maker row founds one and makes it current */}
                <CollectionDrawer className="dt-shelves-drawer" title={lens || (anti ? 'every anti-word' : 'every word')}
                  count={rooms.length} badge={anti && lens ? 'Anti' : undefined} word="dictionaries"
                  open={shelfOpen} onToggle={() => setShelfOpen(v => !v)}
                  items={[{ id: '', name: 'Every word', count: counts.all, master: true, here: !lens && !anti }, ...rooms.map(r => ({ ...r, here: r.id === lens }))]}
                  onPick={enter} onDrop={dropWords}
                  kind="dictionary" onMint={name => { mintCollection(name) }}
                  atriumWord="Every dictionary" onAtrium={() => { setShelfOpen(false); go(HOME) }}
                  {...qh('Your dictionaries', 'The dictionary you are in; open it for every one of them. Press one to read the list through it, or drag words onto one to move them there.')} />
                {groups.map(group => <LibraryDrawer key={group.letter} name={group.letter} count={group.words.length}
                  open={!shutLetters.has(group.letter)} onToggle={() => toggleLetter(group.letter)}>
                  {group.words.map(w => <WordRow key={w._page || w.id} w={w} active={w.id === at} marked={marked.has(w.id)}
                    flying={!!sel.rowProps(w.id)['data-in-flight']} batchN={marked.size > 1 && marked.has(w.id) ? marked.size : 0} grouped={!!activeGroup} act={rowAct}
                    renaming={renaming?.id === w.id} />)}
                </LibraryDrawer>)}
                {sel.marquee.rect && <div className="ofself-marquee" style={sel.marquee.rect} />}
            </LibraryPanel>}
          </>}
          trailing={onHelp ? help.body : <main className="block block--fill dt-main ofself-desk"
            {...qh("The word's page", 'The word and what it means, as one document. With no word open, this is the dictionary in numbers.')}>
            {error && <p className="dt-error" role="alert">{error}</p>}
            {current
              /* THE ROOM AND THE ATRIUM (the shell's ripple): the word page is
                 the open dictionary's room. It settles in as the atrium's
                 pieces fall away, and when another dictionary opens; word to
                 word it stays put. The atrium brings its own way in. */
              ? <RoomArrival id={lens} className="dt-arrive">
                <WordSheet key={`${current._page || current.id}${current.owner ? ':' + (current.access || 'view') : ''}`} word={current} draft={isLocal(current.id)}
                  /* a friend's page is re-keyed when their grant moves: a
                     refused edit remounts the editor on their copy, since the
                     editor is fed local state and would otherwise keep showing
                     the words the platform refused */
                  terms={terms} avoidedTerms={prefs.catchAvoided === false ? [] : avoidedTerms} words={listed}
                  prefs={prefs} onPrefs={savePrefs} onChange={patch} onCommit={commit} onDelete={remove}
                  onDuplicate={() => duplicateWord(current)}
                  me={me} onStamp={stamp} onUnstamp={unstamp}
                  onOpen={openById} onOpenSlug={openBySlug} onKeep={() => keep(current)}
                  /* synonyms and antonyms, both ways; a free word lands in the
                     dictionary in force unless another is picked */
                  onLink={fields => store.link(current.id, fields)} onUnlink={ref => store.unlink(current.id, ref)}
                  dictionaries={shelves.map(s => ({ tag: s.tag, anti: !!s.anti }))} lens={lens}
                  linkOf={linkOf} groups={spaces.groups} activeGroup={activeGroup}
                  onCopy={(into, label) => copyInto(current, into, label)}
                  /* the word page's rail seats Focus — the way in and back */
                  focus={zen} onFocusChange={setZen}
                  /* a word kept from the define sheet is a new word in the list */
                  onMinted={w => { load(); if (w) setNotice(`${w.name} is in the dictionary.`) }}
                  onNotice={setNotice} />
                </RoomArrival>
              : <div className={`dt-nothing${shelved ? '' : ' ofself-arrive'}`}
                  {...qh('Home', 'The dictionary in numbers: how many words, how many committed, how many offered, how many used elsewhere. Each figure is a door — press it to narrow the list to those words.')}>
                  {/* THE HOME IS THE NUMBERS (owner call 2026-09-19, "like
                      missions"): with words in the dictionary the blank page
                      is its dashboard. AN ERROR IS NOT AN EMPTY DICTIONARY: a
                      failed read offers to try again, never to define a first
                      word over words that exist. */}
                  {shelved ? <Home words={listed} shelves={shelves} lens={lens} visits={visits}
                      onShelf={enter} onMintShelf={mintShelf} onReorderShelves={reorderShelves} onDeleteShelf={deleteShelf} onRenameShelf={renameShelf}
                      onSetAnti={setAntiShelf} onDuplicateShelf={duplicateShelf} onExportShelf={exportShelf} onDropWords={dropWords} />
                    : error ? <Button onClick={() => { setError(''); setLoading(true); load() }}
                        {...qh('Try again', 'The dictionary could not be read. This asks the server for it once more.')}>Try again</Button>
                    : null   /* the server has not said yet — nothing is offered over a list that may exist */}
                </div>}
          </main>} />
      <ShareSheet open={!!shareWord} onClose={() => setShareWord(null)} word={shareWord}
        groups={spaces.groups} activeGroup={activeGroup} onCopy={(into, label) => copyInto(shareWord, into, label)} />
      <ExportSheet open={!!exportWord} onClose={() => setExportWord(null)} word={exportWord} terms={committedTerms(words)} />
      {/* THE WALL REGISTERS ITS OWN DOOR (kit 0.253.0): onOpen puts it in the
          shell's registry, so the rail's Personas seat, the console's reading
          rows and the phone menu all open THIS wall on a reading — no right
          upright needed. strip={false}: the seats are the strip. */}
      <IdentityWall docked strip={false} open={identityOpen} onClose={() => setIdentityOpen(false)} onOpen={() => setIdentityOpen(true)}
        storageKey={IDENTITY_KEY} appName="Dictionary" identityName={me?.username || 'You'}
        agents={identityAgents}
        personas={{
          /* the wire: the server signs and proxies Personas' stream; `emit`
             paints the answer and the trace as they arrive */
          send: (text, { emit, mode, incognito }) => agentSend(text, emit, mode, incognito),
          // incognito: a new thread in the chosen posture — the transport forgets the one it was in
          onIncognito: resetAgentThread,
          onStop: agentStop,
          /* THE RECORD IS A CHANGESET: each thread counts the words it put in
             the dictionary, and resuming one draws its offered words as a
             queue — Apply commits, Dismiss deletes, the same two verbs the
             list's Offered filter gives them. Words ride in from this render
             so the count and the queue are live. */
          sessions: () => agentSessions(words),
          onResume: row => agentResume(row, words),
          onResolveAct: async (act, decision) => {
            const id = act.node_id || act.id
            if (!id || !words.some(w => w.id === id)) throw new Error('that word is no longer in the dictionary')
            if (decision === 'apply') await store.commit(id)
            else store.remove(id)
          },
          onNewChat: resetAgentThread,
          greetings: ['Which word are we defining?', 'Name a term you lean on.', 'What do you mean when you say…'],
          /* a cited word opens its page; anything else stays a label */
          nodes: {
            resolve: id => { const w = words.find(x => x.id === id); return w ? { label: w.name || 'Untitled', sub: 'a word' } : null },
            onOpen: id => { if (words.some(x => x.id === id)) openById(id) },
          },
          emptyLabel: 'The Lexicographer is not available right now.',
        }} />
      {/* a find picks by id — a friend's word and yours may share a slug */}
      <Find open={findOpen} onClose={() => setFindOpen(false)} words={listed} current={current?.id} onPick={openById} />
    </Shell>
  </ShellProvider>
}
